import React, { useState, useEffect, useCallback } from 'react';
import { 
  Loader2, 
  Copy, 
  Check, 
  ChevronDown, 
  ChevronUp, 
  Code,
  Filter,
  X,
  Search,
  Calendar,
  User,
  FileText,
  Database,
  Save,
  Play
} from 'lucide-react';
import Card from './Card';
import { useAuth } from '../contexts/AuthContext';
import { 
  getFirestore, 
  collection, 
  query, 
  orderBy, 
  limit,
  getDocs,
  where,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase';

const QueryLog = () => {
  const { currentUser, isAdmin, isAIManager } = useAuth();
  const [queries, setQueries] = useState([]);
  const [filteredQueries, setFilteredQueries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const [copiedId, setCopiedId] = useState(null);
  const [filters, setFilters] = useState({
    userId: '',
    userQuestion: '',
    isSaved: 'all'
  });
  const [sortConfig, setSortConfig] = useState({ 
    key: 'lastRun', 
    direction: 'desc' 
  });
  const [filterDropdowns, setFilterDropdowns] = useState({
    userId: false,
    isSaved: false
  });
  const [uniqueUsers, setUniqueUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
    
  // Internal users to filter out
  const internalUsers = ['yuval331@gmail.com', 'esadot@gmail.com'];
  const isInternalUser = (userId) => {
    if (!userId) return false;
    return userId.endsWith('@tangoai.co') || internalUsers.includes(userId);
  };
  
  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside of any dropdown
      if (!event.target.closest('.filter-dropdown-container')) {
        setFilterDropdowns({
          userId: false,
          isSaved: false
        });
      }
    };
  
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch queries from Firestore
  const fetchQueries = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Build the query
      const queriesRef = collection(db, 'LLM Queries');
      
      // Create query with filters
      let q = query(
        queriesRef,
        orderBy('endTime', 'desc'),
        limit(100) // Limit to last 100 queries for performance
      );

      const querySnapshot = await getDocs(q);
      const fetchedQueries = [];
      const usersSet = new Set();

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        
        // Extract last run time
        let lastRun = null;
        if (data.endTime) {
          if (data.endTime.toDate) {
            lastRun = data.endTime.toDate();
          } else if (data.endTime.seconds) {
            lastRun = new Date(data.endTime.seconds * 1000);
          }
        } else if (data.startTime) {
          if (data.startTime.toDate) {
            lastRun = data.startTime.toDate();
          } else if (data.startTime.seconds) {
            lastRun = new Date(data.startTime.seconds * 1000);
          }
        }

        // For AI Managers (non-admins), filter out internal users
        // For Admins, show all users
        const shouldFilterInternal = isAIManager && !isAdmin;
        
        // Add user to unique users set
        if (data.userId) {
          if (!shouldFilterInternal || !isInternalUser(data.userId)) {
            usersSet.add(data.userId);
          }
        }

        // Skip internal users only for AI Managers (not admins)
        if (!shouldFilterInternal || !isInternalUser(data.userId)) {
          fetchedQueries.push({
            id: doc.id,
            userId: data.userId || 'anonymous',
            userQuestion: data.userQuestion || data.userQuery || '',
            sqlQuery: data.code || data.sqlQuery || '',
            isSaved: data.isSaved || false,
            lastRun: lastRun,
            status: data.status || 'unknown',
            retryCount: data.retryCount || 0,
            llmModel: data.llmModel || 'unknown',
            isFollowUp: data.isFollowUp || false,
            wasInvestigated: data.wasInvestigated || false,
            sanitizationApplied: data.sanitizationApplied || false,
            reviewApplied: data.reviewApplied || false,
            sprayEnabled: data.sprayEnabled || false
          });
        }
      });

      setQueries(fetchedQueries);
      setFilteredQueries(fetchedQueries);
      setUniqueUsers(Array.from(usersSet).sort());

    } catch (err) {
      console.error('Error fetching queries:', err);
      setError(err.message || 'Failed to fetch queries');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, isAIManager]);

  useEffect(() => {
    fetchQueries();
  }, [fetchQueries]);

  // Apply filters and sorting
  useEffect(() => {
    let filtered = [...queries];

    // Apply filters
    if (filters.userId) {
      filtered = filtered.filter(q => q.userId.toLowerCase().includes(filters.userId.toLowerCase()));
    }

    if (filters.userQuestion) {
      filtered = filtered.filter(q => 
        q.userQuestion.toLowerCase().includes(filters.userQuestion.toLowerCase())
      );
    }

    if (filters.isSaved !== 'all') {
      filtered = filtered.filter(q => q.isSaved === (filters.isSaved === 'true'));
    }

    // Apply search term
    if (searchTerm) {
      filtered = filtered.filter(q => 
        q.userQuestion.toLowerCase().includes(searchTerm.toLowerCase()) ||
        q.userId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        q.sqlQuery.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Handle date sorting
      if (sortConfig.key === 'lastRun') {
        aValue = aValue ? aValue.getTime() : 0;
        bValue = bValue ? bValue.getTime() : 0;
      }

      // Handle string sorting
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    setFilteredQueries(filtered);
  }, [queries, filters, sortConfig, searchTerm]);

  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const toggleExpand = (id) => {
    setExpandedRows(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const copyToClipboard = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      userId: '',
      userQuestion: '',
      isSaved: 'all'
    });
    setSearchTerm('');
  };

  const hasActiveFilters = filters.userId || filters.userQuestion || filters.isSaved !== 'all' || searchTerm;

  // NEW: Handle running a query
  const handleRunQuery = (documentId) => {
    // Construct the URL with the query parameter
    // Using llmQueryId to match what index.js expects
    const askUrl = `/ask?llmQueryId=${documentId}`;
    
    // Open in a new tab
    window.open(askUrl, '_blank');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-red-500">
        Error loading queries: {error}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header and Controls */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col space-y-4">
          {/* Search Bar */}
          <div className="flex items-center space-x-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search queries, users, or SQL..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Clear Filters
              </button>
            )}
            <button
              onClick={fetchQueries}
              className="px-4 py-2 text-sm text-white bg-primary rounded-md hover:bg-primary/90"
            >
              Refresh
            </button>
          </div>

          {/* Quick Filters */}
          <div className="flex items-center space-x-4 text-sm">
            <span className="text-gray-500">Quick Filters:</span>
            
            {/* User Filter */}
            <div className="relative filter-dropdown-container">
              <button
                onClick={() => setFilterDropdowns(prev => ({ ...prev, userId: !prev.userId }))}
                className="flex items-center space-x-1 px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                <User className="h-3 w-3" />
                <span>{filters.userId || 'All Users'}</span>
                <ChevronDown className="h-3 w-3" />
              </button>
              {filterDropdowns.userId && (
                <div className="absolute z-10 mt-1 w-48 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
                  <button
                    onClick={() => {
                      handleFilterChange('userId', '');
                      setFilterDropdowns(prev => ({ ...prev, userId: false }));
                    }}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                  >
                    All Users
                  </button>
                  {uniqueUsers.map(user => (
                    <button
                      key={user}
                      onClick={() => {
                        handleFilterChange('userId', user);
                        setFilterDropdowns(prev => ({ ...prev, userId: false }));
                      }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 truncate"
                    >
                      {user}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Saved Filter */}
            <div className="relative filter-dropdown-container">
              <button
                onClick={() => setFilterDropdowns(prev => ({ ...prev, isSaved: !prev.isSaved }))}
                className="flex items-center space-x-1 px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                <Save className="h-3 w-3" />
                <span>{filters.isSaved === 'all' ? 'All Queries' : filters.isSaved === 'true' ? 'Saved Only' : 'Not Saved'}</span>
                <ChevronDown className="h-3 w-3" />
              </button>
              {filterDropdowns.isSaved && (
                <div className="absolute z-10 mt-1 w-40 bg-white border border-gray-200 rounded-md shadow-lg">
                  <button
                    onClick={() => {
                      handleFilterChange('isSaved', 'all');
                      setFilterDropdowns(prev => ({ ...prev, isSaved: false }));
                    }}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                  >
                    All Queries
                  </button>
                  <button
                    onClick={() => {
                      handleFilterChange('isSaved', 'true');
                      setFilterDropdowns(prev => ({ ...prev, isSaved: false }));
                    }}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                  >
                    Saved Only
                  </button>
                  <button
                    onClick={() => {
                      handleFilterChange('isSaved', 'false');
                      setFilterDropdowns(prev => ({ ...prev, isSaved: false }));
                    }}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                  >
                    Not Saved
                  </button>
                </div>
              )}
            </div>

            <span className="text-gray-500">
              Showing {filteredQueries.length} of {queries.length} latest queries (max 100)
            </span>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left">
                  <button
                    onClick={() => handleSort('lastRun')}
                    className="flex items-center space-x-1 text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700"
                  >
                    <Calendar className="h-4 w-4" />
                    <span>Last Run</span>
                    {sortConfig.key === 'lastRun' && (
                      sortConfig.direction === 'desc' ? 
                      <ChevronDown className="h-3 w-3" /> : 
                      <ChevronUp className="h-3 w-3" />
                    )}
                  </button>
                </th>
                <th scope="col" className="px-6 py-3 text-left">
                  <button
                    onClick={() => handleSort('userId')}
                    className="flex items-center space-x-1 text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700"
                  >
                    <User className="h-4 w-4" />
                    <span>User</span>
                    {sortConfig.key === 'userId' && (
                      sortConfig.direction === 'desc' ? 
                      <ChevronDown className="h-3 w-3" /> : 
                      <ChevronUp className="h-3 w-3" />
                    )}
                  </button>
                </th>
                <th scope="col" className="px-6 py-3 text-left">
                  <button
                    onClick={() => handleSort('userQuestion')}
                    className="flex items-center space-x-1 text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700"
                  >
                    <FileText className="h-4 w-4" />
                    <span>Question</span>
                    {sortConfig.key === 'userQuestion' && (
                      sortConfig.direction === 'desc' ? 
                      <ChevronDown className="h-3 w-3" /> : 
                      <ChevronUp className="h-3 w-3" />
                    )}
                  </button>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center space-x-1">
                    <Database className="h-4 w-4" />
                    <span>SQL / Actions</span>
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-left">
                  <button
                    onClick={() => handleSort('isSaved')}
                    className="flex items-center space-x-1 text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700"
                  >
                    <Save className="h-4 w-4" />
                    <span>Saved</span>
                    {sortConfig.key === 'isSaved' && (
                      sortConfig.direction === 'desc' ? 
                      <ChevronDown className="h-3 w-3" /> : 
                      <ChevronUp className="h-3 w-3" />
                    )}
                  </button>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredQueries.map((queryData) => (
                <React.Fragment key={queryData.id}>
                  <tr className={expandedRows[queryData.id] ? 'bg-blue-50 border-l-4 border-blue-500' : 'hover:bg-gray-50'}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(queryData.lastRun)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{queryData.userId}</div>
                      {queryData.isFollowUp && (
                        <span className="text-xs text-gray-500">Follow-up</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 max-w-md">
                        {truncateText(queryData.userQuestion, 100)}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => toggleExpand(queryData.id)}
                          className={`px-3 py-1 text-xs border rounded-md transition-all ${
                            expandedRows[queryData.id] 
                              ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700' 
                              : 'text-blue-600 hover:text-blue-800 border-blue-300 hover:bg-blue-50'
                          }`}
                        >
                          {expandedRows[queryData.id] ? (
                            <>
                              <ChevronUp className="inline h-3 w-3 mr-1" />
                              Hide SQL
                            </>
                          ) : (
                            <>
                              <ChevronDown className="inline h-3 w-3 mr-1" />
                              SQL
                            </>
                          )}
                        </button>
                        <button
                          onClick={() => copyToClipboard(queryData.sqlQuery, queryData.id)}
                          className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
                          title="Copy SQL"
                        >
                          {copiedId === queryData.id ? (
                            <Check className="h-4 w-4 text-green-500" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                        </button>
                        {/* NEW: Run button */}
                        <button
                          onClick={() => handleRunQuery(queryData.id)}
                          className="px-3 py-1 text-xs text-green-600 hover:text-green-800 border border-green-300 hover:bg-green-50 rounded-md transition-all flex items-center"
                          title="Run this query"
                        >
                          <Play className="h-3 w-3 mr-1" />
                          Run
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {queryData.isSaved ? (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                          Saved
                        </span>
                      ) : (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                          Not Saved
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col space-y-1">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          queryData.status === 'completed' ? 'bg-green-100 text-green-800' :
                          queryData.status === 'error' ? 'bg-red-100 text-red-800' :
                          queryData.status === 'processing' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {queryData.status}
                        </span>
                        {queryData.retryCount > 0 && (
                          <span className="text-xs text-gray-500">
                            {queryData.retryCount} retries
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                  {/* Expanded Row */}
                  {expandedRows[queryData.id] && (
                    <tr className="border-l-4 border-blue-500">
                      <td colSpan="6" className="px-6 py-4 bg-blue-50">
                        <div className="space-y-4">
                          {/* Collapse hint */}
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-blue-700 font-medium">
                              Query Details (Click "Hide SQL" above to collapse)
                            </span>
                            <button
                              onClick={() => toggleExpand(queryData.id)}
                              className="text-blue-600 hover:text-blue-800 text-sm underline"
                            >
                              Collapse
                            </button>
                          </div>
                          {/* Full Question */}
                          <div>
                            <h4 className="text-sm font-medium text-gray-700 mb-2">Full Question:</h4>
                            <p className="text-sm text-gray-600 bg-white p-3 rounded border border-gray-200">
                              {queryData.userQuestion}
                            </p>
                          </div>
                          
                          {/* SQL Query */}
                          <div>
                            <div className="flex justify-between items-center mb-2">
                              <h4 className="text-sm font-medium text-gray-700">SQL Query:</h4>
                              <button
                                onClick={() => copyToClipboard(queryData.sqlQuery, `sql-${queryData.id}`)}
                                className="flex items-center space-x-1 px-2 py-1 text-xs text-gray-600 hover:text-gray-800 border border-gray-300 rounded hover:bg-gray-100"
                              >
                                {copiedId === `sql-${queryData.id}` ? (
                                  <>
                                    <Check className="h-3 w-3 text-green-500" />
                                    <span>Copied!</span>
                                  </>
                                ) : (
                                  <>
                                    <Copy className="h-3 w-3" />
                                    <span>Copy SQL</span>
                                  </>
                                )}
                              </button>
                            </div>
                            <div className="bg-white rounded border border-gray-200 overflow-hidden">
                              <pre className="p-4 overflow-x-auto">
                                <code className="text-sm text-gray-800 font-mono">
                                  {queryData.sqlQuery || 'No SQL query available'}
                                </code>
                              </pre>
                            </div>
                          </div>
                          
                          {/* Additional Info */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                              <span className="text-gray-500">Model:</span>
                              <span className="ml-2 text-gray-900">{queryData.llmModel}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Document ID:</span>
                              <span className="ml-2 text-gray-900 font-mono text-xs">{queryData.id}</span>
                            </div>
                            {queryData.wasInvestigated && (
                              <div>
                                <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-amber-100 text-amber-800">
                                  Investigated
                                </span>
                              </div>
                            )}
                            {queryData.sanitizationApplied && (
                              <div>
                                <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800">
                                  Sanitized
                                </span>
                              </div>
                            )}
                            {queryData.reviewApplied && (
                              <div>
                                <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                                  Reviewed
                                </span>
                              </div>
                            )}
                            {queryData.sprayEnabled && (
                              <div>
                                <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-indigo-100 text-indigo-800">
                                  Spray
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
          
          {filteredQueries.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No queries found matching your filters
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QueryLog;